import { useCallback, useContext, useEffect } from "react";
import {
  useRecoilState,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState,
} from "recoil";
import io from "socket.io-client";
import {
  actionState,
  askUserState,
  callFnState,
  chatProfileState,
  chatSettingsInputsState,
  chatSettingsValueState,
  currentThreadIdState,
  elementState,
  firstUserInteraction,
  loadingState,
  messagesState,
  sessionIdState,
  sessionState,
  tasklistState,
  threadIdToResumeState,
  tokenCountState,
} from "./state";
import {
  IAction,
  IElement,
  IMessageElement,
  IStep,
  ITasklistElement,
  IThread,
} from "./types";
import {
  addMessage,
  deleteMessageById,
  updateMessageById,
  updateMessageContentById,
} from "./utils/message";

import { GenAiContext } from "./context";
import type { IToken } from "./useChatData";

const useChatSession = () => {
  const client = useContext(GenAiContext);
  const sessionId = useRecoilValue(sessionIdState);

  const [session, setSession] = useRecoilState(sessionState);

  const resetChatSettingsValue = useResetRecoilState(chatSettingsValueState);
  const setFirstUserInteraction = useSetRecoilState(firstUserInteraction);
  const setLoading = useSetRecoilState(loadingState);
  const setMessages = useSetRecoilState(messagesState);
  const setAskUser = useSetRecoilState(askUserState);
  const setCallFn = useSetRecoilState(callFnState);

  const setElements = useSetRecoilState(elementState);
  const setTasklists = useSetRecoilState(tasklistState);
  const setActions = useSetRecoilState(actionState);
  const setChatSettingsInputs = useSetRecoilState(chatSettingsInputsState);
  const setTokenCount = useSetRecoilState(tokenCountState);
  const [chatProfile, setChatProfile] = useRecoilState(chatProfileState);
  const idToResume = useRecoilValue(threadIdToResumeState);
  const [currentThreadId, setCurrentThreadId] =
    useRecoilState(currentThreadIdState);

  // Use currentThreadId as thread id in websocket header
  useEffect(() => {
    if (session?.socket) {
      session.socket.io.opts.extraHeaders!["X-GenAi-Thread-Id"] =
        currentThreadId || "";
    }
  }, [currentThreadId]);

  const _connect = useCallback(
    ({
      userEnv,
      accessToken,
    }: {
      userEnv: Record<string, string>;
      accessToken?: string;
    }) => {
      const { protocol, host, pathname } = new URL(client.httpEndpoint);
      const uri = `${protocol}//${host}`;
      const path =
        pathname && pathname !== "/"
          ? `${pathname}/ws/socket.io`
          : "/ws/socket.io";

      const socket = io(uri, {
        path,
        extraHeaders: {
          Authorization: accessToken || "",
          "X-GenAi-Client-Type": client.type,
          "X-GenAi-Session-Id": sessionId,
          "X-GenAi-Thread-Id": idToResume || "",
          "user-env": JSON.stringify(userEnv),
          "X-GenAi-Chat-Profile": chatProfile
            ? encodeURIComponent(chatProfile)
            : "",
        },
      });
      setSession((old) => {
        old?.socket?.removeAllListeners();
        old?.socket?.close();
        return {
          socket,
        };
      });

      socket.on("connect", () => {
        socket.emit("connection_successful");
        setSession((s) => ({ ...s!, error: false }));
      });

      socket.on("connect_error", () => {
        setSession((s) => ({ ...s!, error: true }));
      });

      socket.on("task_start", () => {
        setLoading(true);
      });

      socket.on("task_end", () => {
        setLoading(false);
      });

      socket.on("reload", () => {
        socket.emit("clear_session");
        window.location.reload();
      });

      socket.on("resume_thread", (thread: IThread) => {
        let messages: IStep[] = [];
        for (const step of thread.steps) {
          messages = addMessage(messages, step);
        }
        if (thread.metadata?.chat_profile) {
          setChatProfile(thread.metadata?.chat_profile);
        }
        setMessages(messages);
        const elements = thread.elements || [];
        setTasklists(
          (elements as ITasklistElement[]).filter((e) => e.type === "tasklist")
        );
        setElements(
          (elements as IMessageElement[]).filter(
            (e) => ["avatar", "tasklist"].indexOf(e.type) === -1
          )
        );
      });

      socket.on("new_message", (message: IStep) => {
        setMessages((oldMessages) => addMessage(oldMessages, message));
      });

      socket.on(
        "first_interaction",
        (event: { interaction: string; thread_id: string }) => {
          setFirstUserInteraction(event.interaction);
          setCurrentThreadId(event.thread_id);
        }
      );

      socket.on("update_message", (message: IStep) => {
        setMessages((oldMessages) =>
          updateMessageById(oldMessages, message.id, message)
        );
      });

      socket.on("delete_message", (message: IStep) => {
        setMessages((oldMessages) =>
          deleteMessageById(oldMessages, message.id)
        );
      });

      socket.on("stream_start", (message: IStep) => {
        setMessages((oldMessages) => addMessage(oldMessages, message));
      });

      socket.on(
        "stream_token",
        ({ id, token, isSequence, isInput }: IToken) => {
          setMessages((oldMessages) =>
            updateMessageContentById(
              oldMessages,
              id,
              token,
              isSequence,
              isInput
            )
          );
        }
      );

      socket.on("ask", ({ msg, spec }: any, callback: any) => {
        setAskUser({ spec, callback });
        setMessages((oldMessages) => addMessage(oldMessages, msg));

        setLoading(false);
      });

      socket.on("ask_timeout", () => {
        setAskUser(undefined);
        setLoading(false);
      });

      socket.on("clear_ask", () => {
        setAskUser(undefined);
      });

      socket.on("call_fn", ({ name, args }: any, callback: any) => {
        setCallFn({ name, args, callback });
      });

      socket.on("clear_call_fn", () => {
        setCallFn(undefined);
      });

      socket.on("call_fn_timeout", () => {
        setCallFn(undefined);
      });

      socket.on("chat_settings", (inputs: any) => {
        setChatSettingsInputs(inputs);
        resetChatSettingsValue();
      });

      socket.on("element", (element: IElement) => {
        if (!element.url && element.chainlitKey) {
          element.url = client.getElementUrl(element.chainlitKey, sessionId);
        }

        if (element.type === "tasklist") {
          setTasklists((old) => {
            const index = old.findIndex((e) => e.id === element.id);
            if (index === -1) {
              return [...old, element];
            } else {
              return [...old.slice(0, index), element, ...old.slice(index + 1)];
            }
          });
        } else {
          setElements((old) => {
            const index = old.findIndex((e) => e.id === element.id);
            if (index === -1) {
              return [...old, element];
            } else {
              return [...old.slice(0, index), element, ...old.slice(index + 1)];
            }
          });
        }
      });

      socket.on("remove_element", (remove: { id: string }) => {
        setElements((old) => {
          return old.filter((e) => e.id !== remove.id);
        });
        setTasklists((old) => {
          return old.filter((e) => e.id !== remove.id);
        });
      });

      socket.on("action", (action: IAction) => {
        setActions((old) => [...old, action]);
      });

      socket.on("remove_action", (action: IAction) => {
        setActions((old) => {
          const index = old.findIndex((a) => a.id === action.id);
          if (index === -1) return old;
          return [...old.slice(0, index), ...old.slice(index + 1)];
        });
      });

      socket.on("token_usage", (count: number) => {
        setTokenCount((old) => old + count);
      });
    },
    [setSession, sessionId, chatProfile]
  );

  const connect = () => {
    if (import.meta.env.DEV) {
      _connect({ userEnv: "" as any, accessToken: "" });
    }
  }; //useCallback(debounce(_connect, 200), [_connect]);

  const disconnect = useCallback(() => {
    if (session?.socket) {
      session.socket.removeAllListeners();
      session.socket.close();
    }
  }, [session]);

  return {
    connect,
    disconnect,
    session,
    sessionId,
    chatProfile,
    idToResume,
    setChatProfile,
  };
};

export { useChatSession };
